import React, { useState } from 'react';
import { Image as ImageIcon, Type, Palette, Layout, Sparkles, Plus, Trash2, ChevronLeft, ChevronRight, Play, Pause, Copy } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Slider } from './ui/slider';
import { Switch } from './ui/switch';
import MediaPickerModal from './MediaPickerModal';
import AnimationControls, { TEXT_ANIMATIONS, ANIMATION_SPEEDS, ANIMATION_DELAYS } from './AnimationControls';
import LinkSelector from './LinkSelector';
import ButtonEnhancements from './ButtonEnhancements';

const OVERLAY_COLORS = [
  { value: 'none', label: 'No Overlay' },
  { value: 'dark', label: 'Dark', color: 'rgba(0,0,0,0.5)' },
  { value: 'darker', label: 'Darker', color: 'rgba(0,0,0,0.7)' },
  { value: 'light', label: 'Light', color: 'rgba(255,255,255,0.5)' },
  { value: 'gradient-dark', label: 'Gradient (Dark)', color: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)' },
  { value: 'gradient-primary', label: 'Gradient (Purple)', color: 'linear-gradient(135deg, rgba(99,102,241,0.8), rgba(168,85,247,0.8))' },
  { value: 'gradient-warm', label: 'Gradient (Warm)', color: 'linear-gradient(135deg, rgba(251,146,60,0.8), rgba(251,113,133,0.8))' },
  { value: 'gradient-cool', label: 'Gradient (Cool)', color: 'linear-gradient(135deg, rgba(56,189,248,0.8), rgba(99,102,241,0.8))' },
  { value: 'custom', label: 'Custom Color & Opacity' },
];

const TEXT_POSITIONS_H = [
  { value: 'left', label: 'Left' },
  { value: 'center', label: 'Center' },
  { value: 'right', label: 'Right' },
];

const TEXT_POSITIONS_V = [
  { value: 'top', label: 'Top' },
  { value: 'middle', label: 'Middle' },
  { value: 'bottom', label: 'Bottom' },
];

const TEXT_COLORS = [
  { value: 'white', label: 'White' },
  { value: 'black', label: 'Black' },
  { value: 'primary', label: 'Primary (Indigo)' },
];

const WIDTH_OPTIONS = [
  { value: 'full', label: 'Full Width Banner', description: 'Edge to edge (100vw)' },
  { value: 'container', label: 'Container Width', description: 'Matches page width' },
  { value: 'large', label: 'Large (75%)', description: 'Centered large block' },
  { value: 'medium', label: 'Medium (60%)', description: 'Centered medium block' },
  { value: 'small', label: 'Small (45%)', description: 'Compact image with text' },
];

// Fonts available for per-slide typography overrides. Kept in sync with
// HERO_FONT_FAMILIES in BlockEditor.js (do not add new fonts here without
// mirroring them there, otherwise slide fonts won't render on the site).
const SLIDE_FONT_FAMILIES = [
  { value: '', label: 'Inherit from site theme' },
  { value: 'Inter, sans-serif', label: 'Inter (Sans)' },
  { value: '"Open Sans", sans-serif', label: 'Open Sans' },
  { value: 'Poppins, sans-serif', label: 'Poppins' },
  { value: 'Montserrat, sans-serif', label: 'Montserrat' },
  { value: 'Roboto, sans-serif', label: 'Roboto' },
  { value: '"Playfair Display", serif', label: 'Playfair Display (Serif)' },
  { value: 'Merriweather, serif', label: 'Merriweather (Serif)' },
  { value: 'Georgia, serif', label: 'Georgia (Serif)' },
  { value: '"Courier New", monospace', label: 'Courier (Mono)' },
];

// Compact per-slide typography editor. Writes into
// `slide.headlineStyle` / `slide.subheadlineStyle` which the renderer
// applies inline on top of the block-level defaults. Leaving a field
// blank falls back to the block-level typography (or the site theme).
const SlideTypographyRow = ({ label, style, defaultSize, prefix, onPatch }) => (
  <div className="p-2 rounded-md bg-white border border-slate-200 space-y-2">
    <Label className="text-[11px] font-semibold text-slate-700">{label}</Label>
    <div className="grid grid-cols-2 gap-2">
      <div className="space-y-1">
        <Label className="text-[10px] text-slate-500">Font family</Label>
        <Select
          value={style.fontFamily || ''}
          onValueChange={(v) => onPatch({ fontFamily: v === 'inherit' ? '' : v })}
        >
          <SelectTrigger className="h-7 text-xs" data-testid={`${prefix}-family`}>
            <SelectValue placeholder="Inherit" />
          </SelectTrigger>
          <SelectContent>
            {SLIDE_FONT_FAMILIES.map(f => (
              <SelectItem key={f.value || 'inherit'} value={f.value || 'inherit'}>{f.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1">
        <Label className="text-[10px] text-slate-500">Size (px)</Label>
        <Input
          type="number"
          min={10}
          max={140}
          value={style.fontSize || ''}
          onChange={(e) => onPatch({ fontSize: e.target.value ? Number(e.target.value) : undefined })}
          placeholder={String(defaultSize)}
          className="h-7 text-xs"
          data-testid={`${prefix}-size`}
        />
      </div>
      <div className="space-y-1 col-span-2">
        <Label className="text-[10px] text-slate-500">Colour</Label>
        <div className="flex items-center gap-1">
          <input
            type="color"
            value={(style.color || '').startsWith('#') ? style.color : '#111827'}
            onChange={(e) => onPatch({ color: e.target.value })}
            className="w-7 h-7 rounded border cursor-pointer"
            data-testid={`${prefix}-color-swatch`}
          />
          <Input
            value={style.color || ''}
            onChange={(e) => onPatch({ color: e.target.value })}
            placeholder="inherit"
            className="h-7 text-xs font-mono flex-1"
            data-testid={`${prefix}-color-input`}
          />
          {(style.color || style.fontFamily || style.fontSize) && (
            <button
              type="button"
              onClick={() => onPatch({ color: undefined, fontFamily: undefined, fontSize: undefined })}
              className="text-[10px] text-slate-500 hover:text-slate-700 underline"
              title="Reset this slide's typography"
            >
              Reset
            </button>
          )}
        </div>
      </div>
    </div>
  </div>
);

// Helper functions to extract video IDs
const getYouTubeId = (url) => {
  if (!url) return '';
  // Handle youtube-nocookie.com embed URLs
  if (url.includes('youtube-nocookie.com')) {
    const match = url.match(/embed\/([^?&#]+)/);
    return match ? match[1] : '';
  }
  // Handle regular youtube.com and youtu.be URLs
  const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
  const match = url.match(regExp);
  return (match && match[7].length === 11) ? match[7] : '';
};

const getVimeoId = (url) => {
  const regExp = /vimeo.*\/(\d+)/i;
  const match = url?.match(regExp);
  return match ? match[1] : '';
};

const HeroBlockEditor = ({ content, onChange, pages = [], anchors = [] }) => {
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);

  // Support for multiple slides (carousel mode)
  const slides = content.slides || [];
  const autoPlay = content.autoPlay !== false;
  const autoPlaySpeed = content.autoPlaySpeed || 5000;
  const transitionEffect = content.transitionEffect || 'fade';

  const {
    overlay = 'dark',
    textPositionH = 'center',
    textPositionV = 'middle',
    textColor = 'white',
    height = 500,
    width = 'container',
  } = content;

  // Always show carousel controls - use slides if available, or create from legacy content
  // IMPORTANT: Define effectiveSlides BEFORE using it
  const effectiveSlides = slides.length > 0 ? slides.map(slide => ({
    ...slide,
    headline: slide.headline !== undefined ? slide.headline : '',
    subheadline: slide.subheadline !== undefined ? slide.subheadline : '',
    buttonText: slide.buttonText !== undefined ? slide.buttonText : '',
  })) : [{
    imageUrl: content.imageUrl || '',
    backgroundType: content.backgroundType || 'image',
    videoUrl: content.videoUrl || '',
    backgroundColor: content.backgroundColor || '#6366f1',
    headline: content.headline !== undefined ? content.headline : 'Your Headline Here',
    subheadline: content.subheadline !== undefined ? content.subheadline : 'Add a compelling subheadline',
    buttonText: content.buttonText !== undefined ? content.buttonText : 'Learn More',
    buttonUrl: content.buttonUrl || '',
    headlineAnimation: content.headlineAnimation || { entrance: 'fadeInUp', speed: 'normal', delay: '0' },
    subheadlineAnimation: content.subheadlineAnimation || { entrance: 'fadeInUp', speed: 'normal', delay: '200' },
    buttonAnimation: content.buttonAnimation || { entrance: 'fadeInUp', speed: 'normal', delay: '400' },
  }];

  // Current slide from effective slides (now defined after effectiveSlides)
  const currentSlide = effectiveSlides[activeSlideIndex] || effectiveSlides[0];

  const addSlide = () => {
    const newSlide = {
      imageUrl: '',
      backgroundType: 'image',
      videoUrl: '',
      backgroundColor: '#6366f1',
      headline: `Slide ${effectiveSlides.length + 1} Headline`,
      subheadline: 'Add your subheadline here',
      buttonText: 'Learn More',
      buttonUrl: '',
      headlineAnimation: { entrance: 'fadeInUp', speed: 'normal', delay: '0' },
      subheadlineAnimation: { entrance: 'fadeInUp', speed: 'normal', delay: '200' },
      buttonAnimation: { entrance: 'fadeInUp', speed: 'normal', delay: '400' },
    };
    // Use effectiveSlides to handle legacy data migration
    const newSlides = [...effectiveSlides, newSlide];
    onChange({ ...content, slides: newSlides, carouselMode: newSlides.length > 1 });
    setActiveSlideIndex(newSlides.length - 1);
  };

  const removeSlide = (index) => {
    if (effectiveSlides.length <= 1) return;
    const newSlides = effectiveSlides.filter((_, i) => i !== index);
    onChange({ ...content, slides: newSlides, carouselMode: newSlides.length > 1 });
    setActiveSlideIndex(Math.min(activeSlideIndex, newSlides.length - 1));
  };

  const duplicateSlide = (index) => {
    const newSlide = { ...effectiveSlides[index] };
    const newSlides = [...effectiveSlides.slice(0, index + 1), newSlide, ...effectiveSlides.slice(index + 1)];
    onChange({ ...content, slides: newSlides, carouselMode: true });
    setActiveSlideIndex(index + 1);
  };

  const updateSlide = (index, key, value) => {
    // Use effectiveSlides to handle legacy data
    const newSlides = [...effectiveSlides];
    newSlides[index] = { ...newSlides[index], [key]: value };
    onChange({ ...content, slides: newSlides });
  };

  // Update multiple slide fields at once (avoids race condition)
  const updateSlideMultiple = (index, updates) => {
    const newSlides = [...effectiveSlides];
    newSlides[index] = { ...newSlides[index], ...updates };
    onChange({ ...content, slides: newSlides });
  };

  const updateSlideAnimation = (index, animKey, animValue) => {
    const newSlides = [...effectiveSlides];
    newSlides[index] = { ...newSlides[index], [animKey]: animValue };
    onChange({ ...content, slides: newSlides });
  };

  // Merge a partial style patch into `slide.headlineStyle` or
  // `slide.subheadlineStyle`. Undefined keys clear that individual field,
  // so the renderer falls back to the block/site theme default.
  const patchSlideStyle = (index, styleKey, patch) => {
    const newSlides = [...effectiveSlides];
    const merged = { ...(newSlides[index][styleKey] || {}), ...patch };
    // Strip undefined values so the object doesn't accumulate cruft.
    Object.keys(merged).forEach(k => { if (merged[k] === undefined || merged[k] === '') delete merged[k]; });
    newSlides[index] = { ...newSlides[index], [styleKey]: merged };
    onChange({ ...content, slides: newSlides });
  };

  const getWidthStyle = () => {
    switch (width) {
      case 'full': return { width: '100%', maxWidth: 'none' };
      case 'large': return { width: '75%', maxWidth: '1200px', margin: '0 auto' };
      case 'medium': return { width: '60%', maxWidth: '900px', margin: '0 auto' };
      case 'small': return { width: '45%', maxWidth: '600px', margin: '0 auto' };
      default: return { width: '100%', maxWidth: 'none' };
    }
  };

  const updateContent = (key, value) => {
    onChange({ ...content, [key]: value });
  };

  const handleMediaSelect = (media) => {
    // Always use updateSlide since we now use effectiveSlides for all operations
    updateSlide(activeSlideIndex, 'imageUrl', media.url);
    // Also set alt text from media library if available
    if (media.alt_text) {
      updateSlide(activeSlideIndex, 'imageAlt', media.alt_text);
    }
    setShowMediaPicker(false);
  };

  const getOverlayStyle = () => {
    if (overlay === 'none') return {};
    
    // Handle custom overlay
    if (overlay === 'custom') {
      const customColor = content.overlayColor || '#000000';
      const customOpacity = content.overlayOpacity !== undefined ? content.overlayOpacity : 50;
      // Convert hex to rgba
      const hex = customColor.replace('#', '');
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      return { background: `rgba(${r}, ${g}, ${b}, ${customOpacity / 100})` };
    }
    
    const overlayOption = OVERLAY_COLORS.find(o => o.value === overlay);
    if (!overlayOption) return {};
    return { background: overlayOption.color };
  };

  const getTextColorClass = () => {
    switch (textColor) {
      case 'black': return 'text-slate-900';
      case 'primary': return 'text-indigo-600';
      default: return 'text-white';
    }
  };

  return (
    <div className="space-y-6">
      {/* Width Indicator */}
      <div className="flex items-center justify-between bg-slate-100 rounded-lg p-3 border border-slate-200">
        <span className="text-sm text-slate-600 font-medium">
          Block Width: <span className="text-indigo-600">{WIDTH_OPTIONS.find(w => w.value === width)?.label || 'Container'}</span>
        </span>
        {width !== 'full' && width !== 'container' && (
          <span className="text-xs text-slate-500">{WIDTH_OPTIONS.find(w => w.value === width)?.description}</span>
        )}
      </div>

      {/* Carousel Controls */}
      <div className="bg-gradient-to-r from-indigo-500 to-purple-500 rounded-lg p-3 text-white">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium">
            {effectiveSlides.length > 1 ? `Carousel: ${effectiveSlides.length} Slides` : 'Single Image Hero'}
          </span>
          <Button 
            size="sm" 
            variant="secondary" 
            onClick={addSlide}
            className="h-7 text-xs"
          >
            <Plus size={14} className="mr-1" />
            Add Slide
          </Button>
        </div>
        
        {effectiveSlides.length > 1 && (
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 flex-1">
              {effectiveSlides.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveSlideIndex(idx)}
                    className={`h-2 rounded-full transition-all ${
                      idx === activeSlideIndex 
                        ? 'w-8 bg-white' 
                        : 'w-2 bg-white/50 hover:bg-white/70'
                    }`}
                  />
                ))}
              </div>
              <div className="flex items-center gap-2 text-xs">
                <span>Auto-play:</span>
                <Switch
                  checked={autoPlay}
                  onCheckedChange={(v) => updateContent('autoPlay', v)}
                />
              </div>
            </div>
          )}
        </div>

      {/* Live Preview */}
      <div 
        className="relative rounded-lg overflow-hidden mx-auto"
        style={{ 
          height: `${Math.min(height, 400)}px`,
          ...getWidthStyle()
        }}
      >
        {/* Video Background */}
        {currentSlide.backgroundType === 'video' && currentSlide.videoUrl ? (
          <div className="absolute inset-0">
            {currentSlide.videoUrl.includes('youtube.com') || currentSlide.videoUrl.includes('youtu.be') || currentSlide.videoUrl.includes('youtube-nocookie.com') ? (
              <iframe
                src={`https://www.youtube.com/embed/${getYouTubeId(currentSlide.videoUrl)}?autoplay=1&mute=${currentSlide.videoMuted !== false ? 1 : 0}&loop=1&playlist=${getYouTubeId(currentSlide.videoUrl)}&controls=${currentSlide.videoMuted === false ? 1 : 0}&showinfo=0`}
                className="absolute inset-0 w-full h-full"
                style={currentSlide.videoMuted !== false ? { transform: 'scale(1.2)' } : {}}
                frameBorder="0"
                allow="autoplay; encrypted-media; fullscreen"
                allowFullScreen
              />
            ) : currentSlide.videoUrl.includes('vimeo.com') ? (
              <iframe
                src={currentSlide.videoMuted === false
                  ? `https://player.vimeo.com/video/${getVimeoId(currentSlide.videoUrl)}?autoplay=0&loop=0`
                  : `https://player.vimeo.com/video/${getVimeoId(currentSlide.videoUrl)}?autoplay=1&muted=1&loop=1&background=1`
                }
                className="absolute inset-0 w-full h-full"
                style={currentSlide.videoMuted !== false ? { transform: 'scale(1.2)' } : {}}
                frameBorder="0"
                allow="autoplay; fullscreen"
                allowFullScreen
              />
            ) : (
              <video
                src={currentSlide.videoUrl}
                autoPlay
                muted={currentSlide.videoMuted !== false}
                loop={currentSlide.videoLoop !== false}
                playsInline
                className="absolute inset-0 w-full h-full object-cover"
              />
            )}
          </div>
        ) : currentSlide.backgroundType === 'color' || currentSlide.useBackgroundColor ? (
          /* Solid Color Background */
          <div 
            className="absolute inset-0"
            style={{ backgroundColor: currentSlide.backgroundColor || '#6366f1' }}
          />
        ) : currentSlide.imageUrl ? (
          <img 
            src={currentSlide.imageUrl} 
            alt="Hero background"
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center">
            <p className="text-slate-400">Select Image, Video or Color for background</p>
          </div>
        )}
        
        {/* Overlay */}
        {overlay !== 'none' && (
          <div className="absolute inset-0" style={getOverlayStyle()} />
        )}
        
        {/* Content */}
        <div className={`
          absolute inset-0 p-8 flex flex-col
          ${textPositionH === 'left' ? 'items-start text-left' : ''}
          ${textPositionH === 'center' ? 'items-center text-center' : ''}
          ${textPositionH === 'right' ? 'items-end text-right' : ''}
          ${textPositionV === 'top' ? 'justify-start' : ''}
          ${textPositionV === 'middle' ? 'justify-center' : ''}
          ${textPositionV === 'bottom' ? 'justify-end' : ''}
          ${getTextColorClass()}
        `}>
          {currentSlide.useHtml && currentSlide.htmlContent ? (
            /* Custom HTML Content */
            <div 
              className="hero-html-content max-w-4xl"
              dangerouslySetInnerHTML={{ __html: currentSlide.htmlContent }}
            />
          ) : (
            /* Standard Text Content */
            <>
              {currentSlide.headline && (
                <h2 className="text-4xl font-bold mb-4 hero-text-shadow max-w-2xl">
                  {currentSlide.headline}
                </h2>
              )}
              {currentSlide.subheadline && (
                <p className="text-xl mb-6 hero-text-shadow max-w-xl opacity-90">
                  {currentSlide.subheadline}
                </p>
              )}
              {currentSlide.buttonText && (
                <button className="px-6 py-3 bg-white text-slate-900 rounded-lg font-semibold hover:bg-slate-100 transition-colors">
                  {currentSlide.buttonText}
                </button>
              )}
            </>
          )}
        </div>
        
        {/* Slide Navigation in Preview */}
        {effectiveSlides.length > 1 && (
          <>
            <button 
              onClick={() => setActiveSlideIndex(prev => prev > 0 ? prev - 1 : effectiveSlides.length - 1)}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/30 hover:bg-black/50 rounded-full flex items-center justify-center text-white"
            >
              <ChevronLeft size={18} />
            </button>
            <button 
              onClick={() => setActiveSlideIndex(prev => prev < effectiveSlides.length - 1 ? prev + 1 : 0)}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/30 hover:bg-black/50 rounded-full flex items-center justify-center text-white"
            >
              <ChevronRight size={18} />
            </button>
          </>
        )}
      </div>

      {/* Slide Editor */}
      {effectiveSlides.length > 0 && (
        <div className="border border-indigo-200 rounded-lg p-4 bg-indigo-50/50">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-indigo-700 flex items-center gap-2">
              <ImageIcon size={16} />
              Slide {activeSlideIndex + 1} of {effectiveSlides.length}
            </h3>
            <div className="flex items-center gap-1">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => duplicateSlide(activeSlideIndex)}
                className="h-7 w-7 p-0 text-indigo-600"
                title="Duplicate slide"
              >
                <Copy size={14} />
              </Button>
              {effectiveSlides.length > 1 && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => removeSlide(activeSlideIndex)}
                  className="h-7 w-7 p-0 text-red-500"
                  title="Delete slide"
                >
                  <Trash2 size={14} />
                </Button>
              )}
            </div>
          </div>

          {/* Background Selection */}
          <div className="space-y-3 mb-4">
            <Label className="text-xs text-slate-600">Background</Label>
            
            {/* Background Type Toggle */}
            <div className="flex gap-2 mb-2">
              <Button 
                variant={currentSlide.backgroundType === 'image' || (!currentSlide.backgroundType && !currentSlide.useBackgroundColor) ? "default" : "outline"} 
                size="sm"
                onClick={() => {
                  updateSlideMultiple(activeSlideIndex, { backgroundType: 'image', useBackgroundColor: false });
                }}
                className="flex-1 h-8 text-xs"
              >
                <ImageIcon size={14} className="mr-1" />
                Image
              </Button>
              <Button 
                variant={currentSlide.backgroundType === 'video' ? "default" : "outline"} 
                size="sm"
                onClick={() => {
                  updateSlideMultiple(activeSlideIndex, { backgroundType: 'video', useBackgroundColor: false });
                }}
                className="flex-1 h-8 text-xs"
              >
                <Play size={14} className="mr-1" />
                Video
              </Button>
              <Button 
                variant={currentSlide.backgroundType === 'color' || currentSlide.useBackgroundColor ? "default" : "outline"} 
                size="sm"
                onClick={() => {
                  updateSlideMultiple(activeSlideIndex, { backgroundType: 'color', useBackgroundColor: true });
                }}
                className="flex-1 h-8 text-xs"
              >
                <Palette size={14} className="mr-1" />
                Color
              </Button>
            </div>
            
            {/* Image Selection */}
            {(currentSlide.backgroundType === 'image' || (!currentSlide.backgroundType && !currentSlide.useBackgroundColor && currentSlide.backgroundType !== 'video')) && (
              <div className="space-y-2">
                {currentSlide.imageUrl ? (
                  <div className="flex items-center gap-2 p-2 bg-slate-100 rounded-lg">
                    <img 
                      src={currentSlide.imageUrl} 
                      alt="Selected" 
                      className="w-16 h-10 object-cover rounded"
                    />
                    <code className="text-xs text-slate-600 truncate flex-1">{currentSlide.imageUrl.substring(0, 40)}...</code>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="h-8"
                      onClick={() => setShowMediaPicker(true)}
                    >
                      Change
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={() => updateSlide(activeSlideIndex, 'imageUrl', '')}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Input 
                      value={currentSlide.imageUrl || ''} 
                      onChange={(e) => updateSlide(activeSlideIndex, 'imageUrl', e.target.value)}
                      placeholder="Image URL or choose from library..."
                      className="flex-1 h-9"
                    />
                    <Button variant="outline" size="sm" onClick={() => setShowMediaPicker(true)}>
                      Choose
                    </Button>
                  </div>
                )}
              </div>
            )}
            
            {/* Video Selection */}
            {currentSlide.backgroundType === 'video' && (
              <div className="space-y-3">
                <div className="flex gap-2">
                  <Input 
                    value={currentSlide.videoUrl || ''} 
                    onChange={(e) => updateSlide(activeSlideIndex, 'videoUrl', e.target.value)}
                    placeholder="Video URL (MP4, WebM, YouTube, Vimeo)..."
                    className="flex-1 h-9"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="flex items-center justify-between bg-slate-50 px-3 py-2 rounded-lg">
                    <Label className="text-xs text-slate-600">Muted</Label>
                    <Switch
                      checked={currentSlide.videoMuted !== false}
                      onCheckedChange={(v) => updateSlide(activeSlideIndex, 'videoMuted', v)}
                    />
                  </div>
                  <div className="flex items-center justify-between bg-slate-50 px-3 py-2 rounded-lg">
                    <Label className="text-xs text-slate-600">Loop</Label>
                    <Switch
                      checked={currentSlide.videoLoop !== false}
                      onCheckedChange={(v) => updateSlide(activeSlideIndex, 'videoLoop', v)}
                    />
                  </div>
                </div>
                <p className="text-xs text-slate-500">
                  Supports: MP4, WebM, YouTube, Vimeo URLs. Videos auto-play muted by default.
                </p>
              </div>
            )}
            
            {/* Color Picker */}
            {(currentSlide.backgroundType === 'color' || currentSlide.useBackgroundColor) && (
              <div className="space-y-2">
                <div className="flex gap-2 items-center">
                  <div className="relative">
                    <input 
                      type="color" 
                      value={currentSlide.backgroundColor || '#6366f1'}
                      onChange={(e) => updateSlide(activeSlideIndex, 'backgroundColor', e.target.value)}
                      className="w-12 h-9 rounded border border-slate-300 cursor-pointer"
                    />
                  </div>
                  <Input 
                    value={currentSlide.backgroundColor || '#6366f1'}
                    onChange={(e) => updateSlide(activeSlideIndex, 'backgroundColor', e.target.value)}
                    placeholder="#6366f1"
                    className="flex-1 h-9 font-mono text-sm"
                  />
                </div>
                {/* Preset Colors */}
                <div className="flex gap-1 flex-wrap">
                  {['#6366f1', '#8b5cf6', '#ec4899', '#f97316', '#22c55e', '#0ea5e9', '#1e293b', '#64748b'].map(color => (
                    <button
                      key={color}
                      onClick={() => updateSlide(activeSlideIndex, 'backgroundColor', color)}
                      className={`w-6 h-6 rounded border-2 transition-transform hover:scale-110 ${
                        currentSlide.backgroundColor === color ? 'border-slate-900 scale-110' : 'border-transparent'
                      }`}
                      style={{ backgroundColor: color }}
                      title={color}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Text Content */}
          <div className="grid grid-cols-1 gap-3">
            {/* Content Mode Toggle */}
            <div className="flex items-center justify-between p-2 bg-white rounded border">
              <Label className="text-xs text-slate-600">Use Custom HTML</Label>
              <Switch
                checked={currentSlide.useHtml || false}
                onCheckedChange={(v) => updateSlide(activeSlideIndex, 'useHtml', v)}
              />
            </div>
            
            {currentSlide.useHtml ? (
              /* Custom HTML Mode */
              <div className="space-y-2">
                <Label className="text-xs text-slate-600 flex items-center gap-2">
                  <Type size={12} />
                  Custom HTML Content
                </Label>
                <Textarea 
                  value={currentSlide.htmlContent || ''}
                  onChange={(e) => updateSlide(activeSlideIndex, 'htmlContent', e.target.value)}
                  placeholder={`<h1 class="text-5xl font-bold mb-4">Your Title</h1>\n<p class="text-xl mb-6">Your description here</p>\n<a href="#" class="bg-white text-black px-6 py-3 rounded-lg font-semibold">Learn More</a>`}
                  rows={6}
                  className="font-mono text-xs"
                />
                <p className="text-xs text-slate-500">
                  Use Tailwind CSS classes for styling. Content will be rendered inside the hero overlay.
                </p>
              </div>
            ) : (
              /* Standard Text Mode */
              <>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-600 flex items-center gap-2">
                    <Type size={12} />
                    Headline
                  </Label>
                  <Input 
                    value={currentSlide.headline}
                    onChange={(e) => updateSlide(activeSlideIndex, 'headline', e.target.value)}
                    placeholder="Leave empty for no headline"
                    className="font-semibold h-9"
                  />
                  <p className="text-[10px] text-slate-400">Leave empty for no headline</p>
                </div>

                <div className="space-y-1">
                  <Label className="text-xs text-slate-600">Subheadline</Label>
                  <Textarea 
                    value={currentSlide.subheadline}
                    onChange={(e) => updateSlide(activeSlideIndex, 'subheadline', e.target.value)}
                    placeholder="Leave empty for no subheadline"
                    rows={2}
                    className="text-sm"
                  />
                  <p className="text-[10px] text-slate-400">Leave empty for no subheadline</p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-600">Button Text</Label>
                    <Input 
                      value={currentSlide.buttonText}
                      onChange={(e) => updateSlide(activeSlideIndex, 'buttonText', e.target.value)}
                      placeholder="Leave empty for no button"
                      className="h-9"
                    />
                    <p className="text-[10px] text-slate-400">Leave empty for no button</p>
                  </div>
                  <div className="space-y-1">
                    <LinkSelector
                      value={currentSlide.buttonUrl || ''}
                      onChange={(url) => updateSlide(activeSlideIndex, 'buttonUrl', url)}
                      pages={pages}
                      anchors={anchors}
                      label="Button URL"
                      testId={`hero-slide-${activeSlideIndex}-button-url`}
                      disabled={!currentSlide.buttonText}
                    />
                    {currentSlide.buttonText && (
                      <label className="flex items-center gap-1.5 text-xs text-slate-500 pt-1 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={currentSlide.buttonOpenInNewTab || false}
                          onChange={(e) => updateSlide(activeSlideIndex, 'buttonOpenInNewTab', e.target.checked)}
                          className="w-3 h-3 rounded accent-indigo-600"
                          data-testid={`hero-slide-${activeSlideIndex}-button-new-tab`}
                        />
                        Open in new window
                      </label>
                    )}
                  </div>
                </div>

                {/* Corners / Hover / Icon (shown when a button is set) */}
                {currentSlide.buttonText && (
                  <div className="pt-1">
                    <ButtonEnhancements
                      fields={currentSlide}
                      testPrefix={`hero-slide-${activeSlideIndex}-btn`}
                      onPatch={(patch) => Object.entries(patch).forEach(([k, v]) => updateSlide(activeSlideIndex, k, v))}
                    />
                  </div>
                )}

                {/* Per-slide Text Colour — each slide can have its own.
                    Falls back to block-level `textColor` (which now also
                    accepts any hex) when this slide-level value is empty. */}
                <div className="space-y-1">
                  <Label className="text-xs text-slate-600">Slide Text Colour</Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={currentSlide.textColor || textColor || '#ffffff'}
                      onChange={(e) => updateSlide(activeSlideIndex, 'textColor', e.target.value)}
                      className="w-9 h-9 rounded border cursor-pointer"
                      data-testid={`hero-slide-${activeSlideIndex}-text-color`}
                    />
                    <Input
                      value={currentSlide.textColor || ''}
                      onChange={(e) => updateSlide(activeSlideIndex, 'textColor', e.target.value)}
                      placeholder={textColor || '#ffffff'}
                      className="h-9 text-sm font-mono flex-1"
                    />
                    {currentSlide.textColor && (
                      <button
                        type="button"
                        onClick={() => updateSlide(activeSlideIndex, 'textColor', '')}
                        className="text-xs text-slate-500 hover:text-slate-700 underline"
                        title="Reset to default"
                      >
                        Reset
                      </button>
                    )}
                  </div>
                  <p className="text-[10px] text-slate-400">Applies only to this slide. Leave empty to use the block default below.</p>
                </div>

                {/* Per-slide Headline & Subheadline Typography.
                    Writes into `slide.headlineStyle` / `slide.subheadlineStyle`.
                    Blank fields fall back to the block-level defaults, which
                    themselves fall back to the site theme. */}
                <div className="pt-2 space-y-2">
                  <Label className="text-xs font-semibold text-slate-700 flex items-center gap-2">
                    <Type size={12} />
                    Slide {activeSlideIndex + 1} Typography
                  </Label>
                  <SlideTypographyRow
                    label="Headline"
                    style={currentSlide.headlineStyle || {}}
                    defaultSize={48}
                    prefix={`hero-slide-${activeSlideIndex}-headline`}
                    onPatch={(patch) => patchSlideStyle(activeSlideIndex, 'headlineStyle', patch)}
                  />
                  <SlideTypographyRow
                    label="Subheadline"
                    style={currentSlide.subheadlineStyle || {}}
                    defaultSize={20}
                    prefix={`hero-slide-${activeSlideIndex}-subheadline`}
                    onPatch={(patch) => patchSlideStyle(activeSlideIndex, 'subheadlineStyle', patch)}
                  />
                  <p className="text-[10px] text-slate-400">Overrides the block default for this slide only.</p>
                </div>
              </>
            )}
          </div>

          {/* Per-Slide Animation Controls */}
          <div className="mt-4 pt-4 border-t border-indigo-200">
            <div className="flex items-center gap-2 mb-3 text-xs font-semibold text-indigo-700">
              <Sparkles size={12} />
              Slide {activeSlideIndex + 1} Text Animations
            </div>
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-[10px] text-slate-500">Headline</Label>
                  <Select 
                    value={currentSlide.headlineAnimation?.entrance || 'fadeInUp'}
                    onValueChange={(v) => updateSlide(activeSlideIndex, 'headlineAnimation', { ...currentSlide.headlineAnimation, entrance: v })}
                  >
                    <SelectTrigger className="h-7 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TEXT_ANIMATIONS.map(a => (
                        <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] text-slate-500">Subheadline</Label>
                  <Select 
                    value={currentSlide.subheadlineAnimation?.entrance || 'fadeInUp'}
                    onValueChange={(v) => updateSlide(activeSlideIndex, 'subheadlineAnimation', { ...currentSlide.subheadlineAnimation, entrance: v })}
                  >
                    <SelectTrigger className="h-7 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TEXT_ANIMATIONS.map(a => (
                        <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] text-slate-500">Button</Label>
                  <Select 
                    value={currentSlide.buttonAnimation?.entrance || 'fadeInUp'}
                    onValueChange={(v) => updateSlide(activeSlideIndex, 'buttonAnimation', { ...currentSlide.buttonAnimation, entrance: v })}
                  >
                    <SelectTrigger className="h-7 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TEXT_ANIMATIONS.map(a => (
                        <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Carousel Settings */}
      {effectiveSlides.length > 1 && (
        <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
          <div className="flex items-center gap-2 mb-3 text-sm font-semibold text-amber-700">
            <Play size={14} />
            Carousel Settings
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label className="text-xs text-slate-600">Auto-play Speed</Label>
              <Select 
                value={String(autoPlaySpeed)}
                onValueChange={(v) => updateContent('autoPlaySpeed', parseInt(v))}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="3000">Fast (3s)</SelectItem>
                  <SelectItem value="5000">Normal (5s)</SelectItem>
                  <SelectItem value="7000">Slow (7s)</SelectItem>
                  <SelectItem value="10000">Very Slow (10s)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-600">Transition Effect</Label>
              <Select 
                value={transitionEffect}
                onValueChange={(v) => updateContent('transitionEffect', v)}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fade">Fade</SelectItem>
                  <SelectItem value="slide">Slide</SelectItem>
                  <SelectItem value="zoom">Zoom</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      )}

      {/* Layout & Style */}
      <div className="space-y-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-700 pb-2 border-b border-slate-200">
          <Layout size={16} className="text-blue-500" />
          Layout & Style
        </div>

        {/* Width Control - Prominent Position */}
        <div className="space-y-2 pb-4 border-b border-slate-200">
          <Label className="text-xs font-semibold flex items-center gap-1">
            <Layout size={12} />
            Block Width
          </Label>
          <div className="grid grid-cols-5 gap-2">
            {WIDTH_OPTIONS.map((w) => (
              <button
                key={w.value}
                onClick={() => updateContent('width', w.value)}
                className={`p-2 rounded-lg border text-xs font-medium transition-all ${
                  width === w.value
                    ? 'bg-indigo-600 text-white border-indigo-600'
                    : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-300'
                }`}
                title={w.description}
              >
                {w.label.split(' ')[0]}
              </button>
            ))}
          </div>
          <p className="text-xs text-slate-500">
            {WIDTH_OPTIONS.find(w => w.value === width)?.description}
          </p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-xs">Horizontal Position</Label>
            <Select value={textPositionH} onValueChange={(v) => updateContent('textPositionH', v)}>
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TEXT_POSITIONS_H.map(p => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-xs">Vertical Position</Label>
            <Select value={textPositionV} onValueChange={(v) => updateContent('textPositionV', v)}>
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TEXT_POSITIONS_V.map(p => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-xs flex items-center gap-1">
              <Palette size={12} />
              Overlay
            </Label>
            <Select value={overlay} onValueChange={(v) => updateContent('overlay', v)}>
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {OVERLAY_COLORS.map(o => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Custom Overlay Controls */}
            {overlay === 'custom' && (
              <div className="space-y-3 mt-3 p-3 bg-slate-50 rounded-lg border">
                <div className="space-y-2">
                  <Label className="text-xs">Overlay Color</Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={content.overlayColor || '#000000'}
                      onChange={(e) => updateContent('overlayColor', e.target.value)}
                      className="w-10 h-10 rounded border cursor-pointer"
                    />
                    <Input
                      value={content.overlayColor || '#000000'}
                      onChange={(e) => updateContent('overlayColor', e.target.value)}
                      className="h-9 font-mono text-sm"
                      placeholder="#000000"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Opacity: {content.overlayOpacity || 50}%</Label>
                  <Slider
                    value={[content.overlayOpacity || 50]}
                    onValueChange={([v]) => updateContent('overlayOpacity', v)}
                    min={0}
                    max={100}
                    step={5}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label className="text-xs">Default Text Colour (used when a slide has no override)</Label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={typeof textColor === 'string' && textColor.startsWith('#') ? textColor : '#ffffff'}
                onChange={(e) => updateContent('textColor', e.target.value)}
                className="w-9 h-9 rounded border cursor-pointer"
                data-testid="hero-default-text-color"
              />
              <Input
                value={textColor || '#ffffff'}
                onChange={(e) => updateContent('textColor', e.target.value)}
                placeholder="#ffffff"
                className="h-9 text-sm font-mono flex-1"
              />
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-xs">Height: {height}px</Label>
          <Slider
            value={[height]}
            onValueChange={([v]) => updateContent('height', v)}
            min={300}
            max={800}
            step={50}
          />
        </div>
      </div>

      {/* Media Picker */}
      <MediaPickerModal
        open={showMediaPicker}
        onClose={() => setShowMediaPicker(false)}
        onSelect={handleMediaSelect}
      />
    </div>
  );
};

export default HeroBlockEditor;

// Export overlay options for PreviewRenderer
export { OVERLAY_COLORS, WIDTH_OPTIONS };
